/**
 * Synthetic persona leaderboard (v8-visual). Parses the live feed and surfaces
 * per-user activity as avatar cards. v9-interactive adds: transient speech bubbles
 * when a persona acts, and click-to-filter the feed to one persona.
 */

export interface PersonaStats {
  name: string;
  txCount: number;
  eurCents: number;
  fraudAlerts: number;
  sanctionsHits: number;
}

const PERSONAS = ['alice', 'bob', 'eve', 'frank', 'iris', 'jack', 'charlie', 'diana'] as const;

export const PERSONA_COLOR: Record<string, string> = {
  alice: '#4c8dff',
  bob: '#2e9e5b',
  eve: '#d68a1a',
  frank: '#e0463a',
  iris: '#a855f7',
  jack: '#06b6d4',
  charlie: '#6366f1',
  diana: '#ec4899',
};

const EUR_RE = /€([\d,]+(?:\.\d{1,2})?)/;

function parseCents(desc: string): number {
  const m = EUR_RE.exec(desc);
  if (!m) return 0;
  return Math.round(parseFloat(m[1]!.replace(/,/g, '')) * 100);
}

export function computePersonas(
  events: { user: string; scenario: string; description: string }[],
): PersonaStats[] {
  const map = new Map<string, PersonaStats>();
  for (const p of PERSONAS) map.set(p, { name: p, txCount: 0, eurCents: 0, fraudAlerts: 0, sanctionsHits: 0 });
  for (const e of events) {
    const s = map.get(e.user.toLowerCase());
    if (!s) continue;
    s.txCount++;
    s.eurCents += parseCents(e.description);
    if (e.scenario === 'fraud_check') s.fraudAlerts++;
    if (e.scenario === 'sanctions_screen') s.sanctionsHits++;
  }
  return [...map.values()];
}

function fmtEur(cents: number): string {
  if (cents <= 0) return '—';
  const whole = Math.floor(cents / 100);
  const rem = cents % 100;
  return whole >= 10000 ? `€${whole.toLocaleString('en-IE', { maximumFractionDigits: 0 })}` : `€${whole}.${String(rem).padStart(2, '0')}`;
}

// ---- speech bubble state ----
/** Latest event description seen per persona (used to trigger speech bubbles). */
const lastSeen = new Map<string, number>(); // persona → last seq shown as bubble
let bubbleTimer: ReturnType<typeof window.setTimeout> | null = null;

function showBubble(doc: Document, name: string, description: string): void {
  const card = doc.querySelector(`[data-persona="${name}"]`);
  if (!card) return;
  let bubble = card.querySelector('.persona-bubble') as HTMLElement | null;
  if (!bubble) {
    bubble = doc.createElement('div');
    bubble.className = 'persona-bubble';
    card.prepend(bubble);
  }
  bubble.textContent = description.length > 60 ? description.slice(0, 57) + '…' : description;
  bubble.removeAttribute('data-hide');
  bubble.setAttribute('data-show', 'true');
  if (bubbleTimer) window.clearTimeout(bubbleTimer);
  bubbleTimer = window.setTimeout(() => {
    bubble!.removeAttribute('data-show');
    bubble!.setAttribute('data-hide', 'true');
  }, 3200);
}

// ---- feed filter state ----
let personaFilter: string | null = null;

export function getPersonaFilter(): string | null {
  return personaFilter;
}

function setFilter(doc: Document, name: string | null): void {
  personaFilter = name;
  // Update visual state of all cards
  doc.querySelectorAll('[data-persona]').forEach((card) => {
    const p = card.getAttribute('data-persona');
    if (name === null) card.removeAttribute('data-filtered');
    else card.setAttribute('data-filtered', p === name ? 'active' : 'inactive');
  });
  // Show/clear the feed filter indicator
  const indicator = doc.querySelector('[data-feed-filter]');
  if (indicator) {
    indicator.textContent = name ? `Showing: ${name.charAt(0).toUpperCase() + name.slice(1)} only  ×` : '';
    (indicator as HTMLElement).style.display = name ? 'inline-flex' : 'none';
  }
}

export function renderPersonas(doc: Document, stats: PersonaStats[]): void {
  const board = doc.querySelector('[data-personas]');
  if (!board) return;

  const byTx = [...stats].sort((a, b) => b.txCount - a.txCount);
  const topTx = byTx.find((p) => p.txCount > 0);
  const topEur = [...stats].sort((a, b) => b.eurCents - a.eurCents).find((p) => p.eurCents > 0);

  board.replaceChildren(
    ...stats.map((p) => {
      const color = PERSONA_COLOR[p.name] ?? '#93a0b8';
      const isTopTx = topTx?.name === p.name;
      const isTopEur = topEur?.name === p.name;

      const card = doc.createElement('div');
      card.className = 'persona-card';
      card.setAttribute('data-persona', p.name);
      if (personaFilter !== null) {
        card.setAttribute('data-filtered', p.name === personaFilter ? 'active' : 'inactive');
      }

      const av = doc.createElement('div');
      av.className = 'persona-avatar';
      av.style.setProperty('--pa-color', color);
      av.textContent = p.name[0]!.toUpperCase();
      if (isTopTx && p.txCount > 0) av.setAttribute('data-top-tx', 'true');
      if (isTopEur && p.eurCents > 0) av.setAttribute('data-top-eur', 'true');

      const nm = doc.createElement('div');
      nm.className = 'persona-name';
      nm.textContent = p.name.charAt(0).toUpperCase() + p.name.slice(1);

      const st = doc.createElement('div');
      st.className = 'persona-stats';

      function stat(cls: string, text: string): HTMLSpanElement {
        const s = doc.createElement('span');
        s.className = cls;
        s.textContent = text;
        return s;
      }

      st.append(stat('p-stat p-tx', `${p.txCount} tx`));
      st.append(stat('p-stat p-eur', fmtEur(p.eurCents)));
      if (p.fraudAlerts > 0) st.append(stat('p-stat p-fraud', `🔴 ${p.fraudAlerts}`));
      if (p.sanctionsHits > 0) st.append(stat('p-stat p-sanction', `⛔ ${p.sanctionsHits}`));

      // Click to toggle feed filter for this persona
      card.addEventListener('click', () => {
        setFilter(doc, personaFilter === p.name ? null : p.name);
      });

      card.append(av, nm, st);
      return card;
    }),
  );
}

export function updatePersonas(
  doc: Document,
  events: { user: string; scenario: string; description: string; seq?: number }[],
): void {
  const stats = computePersonas(events);
  renderPersonas(doc, stats);

  // Speech bubbles: show a bubble for the most recent new event per persona
  for (const e of events) {
    const name = e.user.toLowerCase();
    if (!PERSONAS.includes(name as typeof PERSONAS[number])) continue;
    const seq = e.seq ?? 0;
    if ((lastSeen.get(name) ?? -1) >= seq) continue;
    lastSeen.set(name, seq);
    showBubble(doc, name, e.description);
    break; // show at most one new bubble per update cycle to avoid spamming
  }
}
